import { captureException } from '@sentry/core';
import DataLoader from 'dataloader';
import { proxy } from 'valtio';
import { type Awareness } from 'y-protocols/awareness';

import type { UserProfileIdentity } from '@canvas/atlassian-client/identity/types';
import { type Identity, type IdentityAccountIdOnly, type ClientId } from '@canvas/model';
import { IdentityKind, type CanvasIdentity } from '@canvas/model/identity';

// @TODO: Make telepointers support anonymous users
export const awarenessFields = {
  telepointers: 't2',
  identity: 'i',
};

export type TelepointerState = {
  identity: Identity;
  entryId: string;
  fieldId: string;
};

export class YDatabaseTelepointers {
  telepointers: Record<number, TelepointerState> = proxy({});
  participants: Record<number, Identity> = proxy({});
  fullIdentityProfiles: DataLoader<string, UserProfileIdentity | undefined> | null;

  /**
   * @param awareness The awareness object to sync telepointers with.
   * @param identitySupplier Provides the account ID of the current user.
   * @param userSupplier Provides full account details for a given ste of account IDs.
   */
  constructor(
    public awareness: Awareness,
    identitySupplier: (() => Promise<Identity>) | null,
    userSupplier:
      | ((accountIds: readonly string[]) => Promise<(UserProfileIdentity | undefined)[]>)
      | null
  ) {
    this.fullIdentityProfiles = userSupplier
      ? new DataLoader(userSupplier, { maxBatchSize: 90 })
      : null;
    awareness.on('change', this.onAwarenessUpdate);

    identitySupplier?.().then((identity) => {
      setAccountId(identity);
      this.awareness.setLocalStateField(awarenessFields.identity, identity.accountId);
    });
  }

  setCursorPosition(entryId?: string, fieldId?: string) {
    this.awareness.setLocalStateField(awarenessFields.telepointers, [entryId, fieldId]);
  }

  private onAwarenessUpdate = (
    { added: addedClientIds, updated: updatedClientIds, removed: deletedClientIds }: any,
    origin: string
  ) => {
    if (origin !== 'local') {
      const states = this.awareness.getStates();

      for (const clientId of [...addedClientIds, ...updatedClientIds]) {
        if (clientId === this.awareness.clientID) {
          continue;
        }

        const accountId =
          typeof states.get(clientId)?.[awarenessFields.identity] === 'string'
            ? states.get(clientId)?.[awarenessFields.identity]
            : // For backwards compatibility with older clients
              states.get(clientId)?.[awarenessFields.identity]?.accountId;

        if (!accountId) {
          continue;
        }

        // Constructing a partial identity with only the accountId in the case we cannot load the full profile
        const partialIdentity = {
          accountId,
          nickname: undefined,
          picture: undefined,
          email: undefined,
          locale: undefined,
          type: IdentityKind.AccountIdOnly,
        } satisfies IdentityAccountIdOnly;

        // Initially set the participant and telepointer with the accountId only: a partial identity
        this.setParticipantsAndTelepointers(clientId, partialIdentity);

        // @TODO: Make telepointers support anonymous users by not attempting to fetch or construct
        // the partial identity for anonymous users, but instead just set the participant and telepointer
        // with the Anonymous Identity type
        this.fullIdentityProfiles
          ?.load(accountId)
          .then((identityProfile) => {
            // We need to check state again, in case it has since been deleted
            if (identityProfile) {
              // If we were able to load the full profile, we update the participant and
              // telepointer with their full identity
              const identity = this.getIdentityFromProfile(identityProfile);
              this.setParticipantsAndTelepointers(clientId, identity);
            }
          })
          .catch((e) => captureException(e));
      }

      for (const clientId of deletedClientIds) {
        delete this.telepointers[clientId];
        delete this.participants[clientId];
      }
    }
  };

  private getIdentityFromProfile(identityProfile: UserProfileIdentity): CanvasIdentity {
    return {
      accountId: identityProfile.id,
      nickname: identityProfile.nickname ?? identityProfile.name,
      picture: identityProfile.picture,
      email: identityProfile.email,
      locale: identityProfile.locale,
      type: IdentityKind.CanvasId,
    } satisfies CanvasIdentity;
  }

  /**
   * Given a clientId and an identity, sets the participant and telepointer state
   * @param clientId The clientId of the participant
   * @param identity The identity of the participant
   */
  private setParticipantsAndTelepointers(clientId: ClientId, identity: Identity) {
    const state = this.awareness.getStates().get(clientId);

    const identityState = state?.[awarenessFields.identity];
    if (identityState) {
      this.participants[clientId] = identity;
    }

    const telepointers = state?.[awarenessFields.telepointers];
    if (Array.isArray(telepointers)) {
      const [entryId, fieldId] = telepointers;
      this.telepointers[clientId] = {
        entryId,
        fieldId,
        identity,
      };
    }
  }
}

// For backwards compat with old clients
function setAccountId(identity: Identity | undefined) {
  if (identity && 'account_id' in identity) {
    identity.accountId = identity.account_id as string;
  }
}
